import type { Resolution, ThreadMessage } from './compliance-store';
import { getAnalysis } from './compliance-engine';
import type { InvoiceId } from './invoices-data';
import { getPlan } from './recommendations';

/**
 * Invoices the agent resolved by itself when they were ingested, so the queue
 * starts with them in Resolved and the open queue is unchanged. Border States
 * BS-22188 bills Net 30 against Net 45 in the agreement; price and quantity
 * match, so the agent scheduled payment on contract terms and told the
 * supplier without holding anything.
 */
const AUTO_RESOLVED_IDS: InvoiceId[] = ['BS-22188'];

export interface AutoResolvedSeed {
  resolutions: Partial<Record<InvoiceId, Resolution>>;
  threads: Partial<Record<InvoiceId, ThreadMessage[]>>;
}

export function autoResolvedSeed(): AutoResolvedSeed {
  const seed: AutoResolvedSeed = { resolutions: {}, threads: {} };
  for (const id of AUTO_RESOLVED_IDS) {
    const plan = getPlan(id);
    const received = getAnalysis(id).invoice.invoiceDate.getTime();
    seed.resolutions[id] = {
      kind: 'recommendation',
      at: received,
      stage: 2,
      auto: true,
    };
    const followUp = plan.followUp;
    if (!followUp) continue;
    const script = [
      // The notice goes out on receipt rather than "just now".
      { ...followUp.sent, whenLabel: 'On receipt' },
      ...followUp.acknowledged,
      ...followUp.closed,
    ];
    seed.threads[id] = script.map((message, index) => ({
      id: `${id}-m${index + 1}`,
      from: message.from,
      author: message.author,
      body: message.body,
      whenLabel: message.whenLabel,
      email: index === 0 && plan.email ? plan.email : undefined,
    }));
  }
  return seed;
}
